import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Modal, Row, Spin, Tag, Timeline, Tooltip } from 'antd';
import {
  ArrowDownOutlined, ArrowUpOutlined, CalendarOutlined, CarOutlined, DownloadOutlined,
  FileOutlined, FilePdfOutlined, MessageOutlined, PaperClipOutlined, RightOutlined,
  SwapOutlined, UserSwitchOutlined, WarningOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { describeOpsError, downloadOpsAttachment } from '../../util/opsApi';

/**
 * Read-only detail of one Ops ticket (payload of GET /integration/support-tickets/:id),
 * laid out like the Ops app's own ticket page (iot-ops frontend
 * SupportTicketDetailPage): header, Complaint card, Attachments grid, Timeline.
 * Ops-internal sections (site-visit billing, troubleshooting guide, action
 * buttons) are intentionally not shown to clients.
 *
 * Attachments: Ops serves files only through its authenticated download route,
 * so images are fetched (once each) when the ticket opens and shown as
 * thumbnails; other files are fetched only when clicked.
 */

export const EMPTY = '—';

export const formatDate = (value, withTime) => {
  if (!value) return EMPTY;
  const m = moment(value);
  return m.isValid() ? m.format(withTime ? 'DD MMM YYYY HH:mm' : 'DD MMM YYYY') : EMPTY;
};

// Same look as Ops' statusTagStyle: pastel master colour, dark text.
export const statusTagStyle = (colorHex) => ({
  backgroundColor: colorHex || '#f5f5f5',
  color: '#1f2937',
  border: '1px solid rgba(0,0,0,0.08)',
});

export const StatusTag = ({ status }) => {
  if (!status || !status.name) return EMPTY;
  return <Tag style={statusTagStyle(status.colorHex)}>{status.name}</Tag>;
};

const IMAGE_MIME = /^image\/(png|jpe?g|gif|webp|bmp)$/i;
const isImage = (a) => IMAGE_MIME.test(a.mimeType || '');

const updateIcon = (kind) => {
  switch (kind) {
    case 'STATUS_CHANGE': return <SwapOutlined />;
    case 'TARGET_DATE_CHANGE': return <CalendarOutlined />;
    case 'ESCALATION': return <ArrowUpOutlined style={{ color: '#fa541c' }} />;
    case 'DE_ESCALATION': return <ArrowDownOutlined style={{ color: '#52c41a' }} />;
    case 'ASSIGNMENT': return <UserSwitchOutlined />;
    case 'VISIT_NOTE': return <CarOutlined />;
    default: return <MessageOutlined />;
  }
};

// Mirrors Ops' actorLabel. A missing name is labelled by side, not guessed.
const actorLabel = (u) => (u.actorType === 'ASSETPRO'
  ? `${u.actorName || 'AssetPro'} (AssetPro)`
  : (u.actor && u.actor.name) || 'Ops team');

const uploaderLabel = (a) => (a.source === 'ASSETPRO'
  ? (a.uploaderName || 'AssetPro')
  : (a.uploadedBy && a.uploadedBy.name) || 'Ops');

const byNewest = (a, b) => {
  const occurred = moment(b.occurredAt).valueOf() - moment(a.occurredAt).valueOf();
  return occurred !== 0 ? occurred : moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf();
};

const joinDefined = (parts, sep) => {
  const kept = parts.filter(p => p !== null && p !== undefined && String(p).trim() !== '');
  return kept.length ? kept.join(sep) : EMPTY;
};

/** One attachment as a tile: image thumbnail, or PDF / file icon. */
function AttachmentTile({ attachment, file, width, height, showMeta, onOpen }) {
  const frame = {
    width, height, borderRadius: 6, border: '1px solid #f0f0f0', background: '#fafafa',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    overflow: 'hidden', cursor: 'pointer',
  };
  const pdf = attachment.mimeType === 'application/pdf';
  const ext = ((attachment.fileName || '').split('.').pop() || '').toUpperCase().slice(0, 5);

  let body;
  if (isImage(attachment)) {
    if (file && file.url) {
      body = <img src={file.url} alt={attachment.fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    } else if (file && file.error) {
      body = <span style={{ fontSize: 11, color: '#cf1322', padding: 4, textAlign: 'center' }}>Not loaded — click to retry</span>;
    } else {
      body = <Spin size="small" />;
    }
  } else {
    body = file && file.loading ? <Spin size="small" /> : (
      <>
        {pdf ? <FilePdfOutlined style={{ fontSize: 28, color: '#cf1322' }} /> : <FileOutlined style={{ fontSize: 28, color: '#8c8c8c' }} />}
        <span style={{ fontSize: 11 }}>{pdf ? 'PDF' : ext || 'FILE'}</span>
      </>
    );
  }

  return (
    <div style={{ width, textAlign: 'center' }}>
      <Tooltip title={attachment.caption || attachment.fileName}>
        <div style={frame} onClick={() => onOpen(attachment)}>{body}</div>
      </Tooltip>
      {showMeta && (
        <>
          <Tooltip title={attachment.fileName}>
            <div style={{ fontSize: 11, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {attachment.fileName || EMPTY}
            </div>
          </Tooltip>
          <div style={{ fontSize: 11, color: '#8c8c8c' }}>
            {uploaderLabel(attachment)}{' '}
            <a title="Download" onClick={() => onOpen(attachment, { download: true })}><DownloadOutlined /></a>
          </div>
          {file && file.error && <div style={{ fontSize: 11, color: '#cf1322' }}>{file.error}</div>}
        </>
      )}
    </div>
  );
}

export default function OpsTicketDetail({ detail, token, onBack }) {
  const [files, setFiles] = useState({}); // attachmentId -> { loading, url, error }
  const [previewIndex, setPreviewIndex] = useState(null); // index into image attachments
  const urlsRef = useRef([]);

  useEffect(() => () => urlsRef.current.forEach(u => URL.revokeObjectURL(u)), []);

  const fetchAttachment = async (attachment) => {
    setFiles(prev => ({ ...prev, [attachment.id]: { loading: true } }));
    try {
      const response = await downloadOpsAttachment(token, attachment.id);
      const url = URL.createObjectURL(response.data);
      urlsRef.current.push(url);
      setFiles(prev => ({ ...prev, [attachment.id]: { url } }));
      return url;
    } catch (e) {
      setFiles(prev => ({ ...prev, [attachment.id]: { error: describeOpsError(e) } }));
      return null;
    }
  };

  // Thumbnails: each image attachment is fetched once when the ticket detail arrives.
  const detailData = detail && detail.data;
  useEffect(() => {
    const list = (detailData && Array.isArray(detailData.attachments)) ? detailData.attachments : [];
    list.filter(isImage).forEach(a => { fetchAttachment(a); });
    // Keyed on the detail payload only: runs once when the ticket detail arrives.
  }, [detailData]);

  const backButton = <Button icon="arrow-left" onClick={onBack}>Back</Button>;
  if (!detail || detail.loading) return <div>{backButton}<div style={{ padding: 40, textAlign: 'center' }}><Spin /></div></div>;
  if (detail.error) return <div>{backButton}<Alert style={{ marginTop: 12 }} type="error" showIcon message={detail.error} /></div>;

  const ticket = detail.data || {};
  const updates = Array.isArray(ticket.updates) ? [...ticket.updates].sort(byNewest) : [];
  const attachments = Array.isArray(ticket.attachments) ? ticket.attachments : [];
  const images = attachments.filter(isImage);

  const attachmentsByUpdate = {};
  attachments.forEach(a => {
    if (a.updateId) (attachmentsByUpdate[a.updateId] = attachmentsByUpdate[a.updateId] || []).push(a);
  });

  const saveFile = (attachment, url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName || 'attachment';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const openAttachment = async (attachment, opts = {}) => {
    const current = files[attachment.id];
    if (current && current.loading) return;
    const url = (current && current.url) || await fetchAttachment(attachment);
    if (!url) return;
    if (isImage(attachment) && !opts.download) {
      setPreviewIndex(images.findIndex(a => a.id === attachment.id));
    } else {
      saveFile(attachment, url);
    }
  };

  const previewed = previewIndex !== null ? images[previewIndex] : null;
  const previewFile = previewed ? files[previewed.id] : null;

  return (
    <div>
      <Row type="flex" justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          {backButton}
          <span style={{ fontSize: 18, fontWeight: 600, margin: '0 10px', verticalAlign: 'middle' }}>{ticket.ticketNo || EMPTY}</span>
          <StatusTag status={ticket.status} />
          {ticket.isEscalated && (
            <Tooltip title={ticket.escalationReason}>
              <Tag color="volcano"><WarningOutlined /> Escalated</Tag>
            </Tooltip>
          )}
          {ticket.source && (
            <Tag>{ticket.source === 'ASSETPRO' ? 'Raised by client (AssetPro)' : ticket.source === 'OPS' ? 'Raised by AssetPro team' : ticket.source}</Tag>
          )}
        </Col>
      </Row>

      <Card size="small" title="Complaint" style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="Details" span={2}>{ticket.detailsOfComplaint || EMPTY}</Descriptions.Item>
          <Descriptions.Item label="Cause">{ticket.causeOfDamage || EMPTY}</Descriptions.Item>
          <Descriptions.Item label="Complaint Date">{formatDate(ticket.complaintDate, true)}</Descriptions.Item>
          <Descriptions.Item label="AssetPro Job Card">{ticket.assetproJobCardNo || EMPTY}</Descriptions.Item>
          <Descriptions.Item label="AssetPro Complaint">{ticket.assetproComplaintNo || EMPTY}</Descriptions.Item>
          <Descriptions.Item label="Asset Code">{ticket.assetCode || EMPTY}</Descriptions.Item>
          <Descriptions.Item label="Asset Type">{joinDefined([ticket.assetType, ticket.assetSubType], ' / ')}</Descriptions.Item>
          <Descriptions.Item label="Location">{joinDefined([ticket.location, ticket.subLocation], ' / ')}</Descriptions.Item>
          <Descriptions.Item label="Owner Code">{ticket.ownerCode || EMPTY}</Descriptions.Item>
          <Descriptions.Item label="Target Date">{formatDate(ticket.targetDate)}</Descriptions.Item>
          <Descriptions.Item label="Next Review">{formatDate(ticket.nextReviewDate)}</Descriptions.Item>
          <Descriptions.Item label="Assigned To">{(ticket.assignedTo && ticket.assignedTo.name) || EMPTY}</Descriptions.Item>
          <Descriptions.Item label="Last Updated">{formatDate(ticket.updatedAt, true)}</Descriptions.Item>
          {ticket.closedAt && <Descriptions.Item label="Closed At" span={2}>{formatDate(ticket.closedAt, true)}</Descriptions.Item>}
          {ticket.cancelReason && <Descriptions.Item label="Cancel Reason" span={2}>{ticket.cancelReason}</Descriptions.Item>}
          {ticket.statusRemark && <Descriptions.Item label="Status Remark" span={2}>{ticket.statusRemark}</Descriptions.Item>}
        </Descriptions>
      </Card>

      <Card size="small" title={<span><PaperClipOutlined /> Attachments ({attachments.length})</span>} style={{ marginBottom: 16 }}>
        {attachments.length === 0 ? (
          <span style={{ color: '#8c8c8c' }}>No photos or documents attached.</span>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {attachments.map(a => (
              <AttachmentTile key={a.id} attachment={a} file={files[a.id]} width={132} height={100} showMeta onOpen={openAttachment} />
            ))}
          </div>
        )}
      </Card>

      <Card size="small" title="Timeline">
        {updates.length === 0 ? <span style={{ color: '#8c8c8c' }}>No updates recorded on this ticket.</span> : (
          <Timeline style={{ marginTop: 8 }}>
            {updates.map(u => (
              <Timeline.Item key={u.id} dot={updateIcon(u.kind)}>
                <div>
                  <strong>{actorLabel(u)}</strong>
                  <span style={{ color: '#8c8c8c', fontSize: 12, margin: '0 8px' }}>{formatDate(u.occurredAt || u.createdAt, true)}</span>
                  {u.kind === 'STATUS_CHANGE' && u.statusTo && (
                    <>
                      {u.statusFrom && <StatusTag status={u.statusFrom} />}
                      {u.statusFrom && <span style={{ color: '#8c8c8c', marginRight: 8 }}>→</span>}
                      <StatusTag status={u.statusTo} />
                    </>
                  )}
                  {u.kind === 'ESCALATION' && <Tag color="volcano">Escalated</Tag>}
                  {u.kind === 'DE_ESCALATION' && <Tag color="green">De-escalated</Tag>}
                  {u.kind === 'ASSIGNMENT' && (
                    <>
                      <Tag>{(u.assignedFrom && u.assignedFrom.name) || 'Unassigned'}</Tag>
                      <span style={{ color: '#8c8c8c', marginRight: 8 }}>→</span>
                      <Tag color="blue">{(u.assignedTo && u.assignedTo.name) || 'Unassigned'}</Tag>
                    </>
                  )}
                  {u.kind === 'TARGET_DATE_CHANGE' && (
                    <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                      Target: {formatDate(u.previousTargetDate)} → {formatDate(u.newTargetDate)}
                    </span>
                  )}
                </div>
                {u.statusRemark && <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{u.statusRemark}</div>}
                {u.body && <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{u.body}</div>}
                {u.reviewDate && <div style={{ color: '#8c8c8c', fontSize: 12 }}>Next review: {formatDate(u.reviewDate)}</div>}
                {(attachmentsByUpdate[u.id] || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                    {attachmentsByUpdate[u.id].map(a => (isImage(a)
                      ? <AttachmentTile key={a.id} attachment={a} file={files[a.id]} width={84} height={64} onOpen={openAttachment} />
                      : (
                        <a key={a.id} onClick={() => openAttachment(a)} style={{ fontSize: 12 }}>
                          <PaperClipOutlined /> {a.fileName}
                        </a>
                      )))}
                  </div>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Card>

      <Modal
        visible={!!previewed}
        title={previewed ? `${previewed.fileName || 'Image'} (${previewIndex + 1} of ${images.length})` : ''}
        width="80%"
        onCancel={() => setPreviewIndex(null)}
        footer={previewed ? [
          <Button key="prev" icon="left" disabled={previewIndex === 0}
            onClick={() => setPreviewIndex(i => i - 1)}>Previous</Button>,
          <Button key="next" disabled={previewIndex >= images.length - 1}
            onClick={() => setPreviewIndex(i => i + 1)}>Next <RightOutlined /></Button>,
          <Button key="dl" icon="download" onClick={() => openAttachment(previewed, { download: true })}>Download</Button>,
        ] : null}
      >
        {previewed && previewed.caption && <div style={{ marginBottom: 8 }}>{previewed.caption}</div>}
        {previewFile && previewFile.url
          ? <img src={previewFile.url} alt={previewed.fileName} style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', margin: '0 auto' }} />
          : previewFile && previewFile.error
            ? <Alert type="error" showIcon message={previewFile.error} />
            : <div style={{ textAlign: 'center' }}><Spin /></div>}
      </Modal>
    </div>
  );
}
